import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../assets/assetWorkflow.css';
import './screen.css';
import { useDeal } from '../deals/DealContext.jsx';
import { withDealQuery } from '../deals/registry';
import { useAssistantPageExtras } from '../components/assistant/AssistantContext';
import { useAuth } from '../auth/AuthContext';
import {
  screenApi,
  fmtUnit,
  waitForScreen,
  screenAssistantContext,
  thresholdText,
  rankDeals,
  topReason,
  SHORT_OUTCOME,
} from './api.js';
import { CriteriaTable, OutcomePill, RiskList, ValueBridge } from './ValueRisks.jsx';

/** Dashboard metric columns (Luther: PDP PV10, net CF, ARO %, ABS note). */
const COLS = ['pdpPv10', 'l12Ncf', 'aroPctPdpPv', 'absNote'];
const COL_LABEL = {
  pdpPv10: 'PDP PV10',
  l12Ncf: 'Net CF (L12M)',
  aroPctPdpPv: 'ARO % of PDP',
  absNote: 'ABS note',
};

function critOf(r, key) {
  return (r?.criteria || []).find((c) => c.key === key);
}

/** Open a deal on its first meeting step (Analysis). */
function openDeal(selectDeal, dealId) {
  selectDeal(dealId);
  window.location.hash = withDealQuery('analysis', dealId);
}

/** Landing dashboard: every deal screened against the buy box, ranked (internal). */
export default function QuickScreen() {
  const auth = useAuth();
  const { selectDeal } = useDeal();
  const { setPageExtras, clearPageExtras } = useAssistantPageExtras();
  const [deals, setDeals] = useState([]);
  const [crit, setCrit] = useState(null);
  const [open, setOpen] = useState(null);
  const [critOpen, setCritOpen] = useState(false);
  const [running, setRunning] = useState({});
  const [err, setErr] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([screenApi.deals(), screenApi.criteria()]);
      setDeals(d.deals);
      setCrit(c);
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Stale rows re-run server-side; poll until they settle.
  const serverRunning = deals.some((d) => d.running);
  useEffect(() => {
    if (!serverRunning) return undefined;
    const t = setTimeout(refresh, 3000);
    return () => clearTimeout(t);
  }, [serverRunning, deals, refresh]);

  const ranked = useMemo(() => rankDeals(deals), [deals]);

  useEffect(() => {
    if (!deals.length) return undefined;
    setPageExtras({
      tab: 'deal-dashboard',
      deals: ranked.map((d) => ({
        label: d.label,
        kind: d.kind,
        ...(screenAssistantContext(d) || { outcome: 'not run' }),
      })),
    });
    return () => clearPageExtras();
  }, [deals.length, ranked, setPageExtras, clearPageExtras]);

  const runOne = useCallback(
    async (dealId) => {
      setRunning((m) => ({ ...m, [dealId]: true }));
      try {
        await waitForScreen(dealId, { start: true });
      } catch (e) {
        setErr(`${dealId}: ${e.message}`);
      } finally {
        setRunning((m) => ({ ...m, [dealId]: false }));
        refresh();
      }
    },
    [refresh],
  );

  const runAll = useCallback(async () => {
    // Server runs at most two ABS solves at a time; kick all, then poll each.
    await Promise.all(deals.map((d) => runOne(d.dealId)));
  }, [deals, runOne]);

  const anyRunning = useMemo(
    () => Object.values(running).some(Boolean) || deals.some((d) => d.running),
    [running, deals],
  );

  const counts = useMemo(() => {
    const m = { pass: 0, review: 0, outside: 0, nodata: 0 };
    for (const d of deals) {
      const o = d.result?.outcome;
      if (o in m) m[o] += 1;
    }
    return m;
  }, [deals]);

  const critDefs = crit?.criteria || {};
  return (
    <div className="qs-page" data-testid="quick-screen">
      <div className="qs-head">
        <h2>All deals — screened against your buy box</h2>
        <div className="qs-head-actions">
          <a
            className="aw-btn"
            href="#buy-box"
            data-testid="qs-criteria-link"
            onClick={(e) => {
              e.preventDefault();
              setCritOpen(true);
              document.getElementById('buy-box')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            Buy-box criteria
          </a>
          <button
            type="button"
            className="aw-btn aw-btn-amber"
            disabled={anyRunning}
            onClick={runAll}
            data-testid="qs-run-all"
          >
            {anyRunning ? 'Screening…' : 'Run screen on all deals'}
          </button>
        </div>
      </div>
      <div className="qs-counts" data-testid="qs-counts">
        {['pass', 'review', 'outside', 'nodata'].map((k) => (
          <span key={k} className={`qs-count qs-count-${k}`}>
            <b>{counts[k]}</b> {SHORT_OUTCOME[k]}
          </span>
        ))}
      </div>
      <p className="aw-small qs-muted">
        Same criteria for every deal. ARO comes from the configured ARO (D2D estimate unless
        overridden); ABS is the base solve. Outcomes are screening labels, not recommendations.
        Click a deal to open its workflow.
      </p>
      {err && (
        <div className="abs-aro-banner" role="status">
          {err}
        </div>
      )}
      <div className="qs-table-wrap">
        <table className="qs-table qs-all" data-testid="qs-all">
          <thead>
            <tr>
              <th className="qs-rank">#</th>
              <th>Deal</th>
              <th>Outcome</th>
              {COLS.map((k) => (
                <th
                  key={k}
                  className="qs-num"
                  title={critDefs[k] ? thresholdText(critDefs[k]) : ''}
                >
                  {COL_LABEL[k]}
                </th>
              ))}
              <th>Top reason</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ranked.map((d, idx) => {
              const r = d.result;
              const busy = running[d.dealId] || d.running;
              const teaser = d.kind === 'teaser';
              const toggle = () => setOpen(open === d.dealId ? null : d.dealId);
              return (
                <React.Fragment key={d.dealId}>
                  <tr
                    data-testid={`qs-row-${d.dealId}`}
                    onClick={() => (teaser ? toggle() : openDeal(selectDeal, d.dealId))}
                    className="qs-click"
                    title={teaser ? 'Teaser — show screen detail' : `Open ${d.label}`}
                  >
                    <td className="qs-rank mono">{idx + 1}</td>
                    <td className="qs-deal">
                      <b>{d.label}</b>
                      {teaser && <span className="aw-small qs-muted"> · teaser</span>}
                    </td>
                    <td className="qs-nowrap">
                      {busy ? (
                        <span className="aw-small">running…</span>
                      ) : r ? (
                        <OutcomePill
                          outcome={r.outcome}
                          label={SHORT_OUTCOME[r.outcome] || r.outcomeLabel}
                        />
                      ) : (
                        <span className="aw-small qs-muted">not run</span>
                      )}
                      {!busy && d.stale && <div className="aw-small qs-muted">inputs changed</div>}
                      {d.job?.state === 'error' && (
                        <div className="aw-small qs-cell-outside">run failed: {d.job.error}</div>
                      )}
                      {r?.absError && <div className="aw-small qs-cell-outside">ABS failed</div>}
                    </td>
                    {COLS.map((k) => {
                      const c = critOf(r, k);
                      return (
                        <td key={k} className={`mono qs-num ${c ? `qs-cell-${c.status}` : ''}`}>
                          {c ? fmtUnit(c.value, c.unit) : '—'}
                        </td>
                      );
                    })}
                    <td className="aw-small qs-reason">{topReason(r)}</td>
                    <td className="qs-row-actions">
                      <button
                        type="button"
                        className="aw-btn"
                        data-testid={`qs-details-${d.dealId}`}
                        disabled={!r}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle();
                        }}
                      >
                        {open === d.dealId ? 'Hide' : 'Details'}
                      </button>
                      <button
                        type="button"
                        className="aw-btn"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          runOne(d.dealId);
                        }}
                      >
                        Run
                      </button>
                    </td>
                  </tr>
                  {open === d.dealId && r && (
                    <tr className="qs-detail">
                      <td colSpan={COLS.length + 5}>
                        {r.reason && <div className="aw-small">{r.reason}</div>}
                        <div className="qs-grid">
                          <div>
                            <h4>Value bridge</h4>
                            <ValueBridge rows={r.valueBridge} />
                          </div>
                          <div>
                            <h4>Risks</h4>
                            <RiskList risks={r.risks} />
                          </div>
                        </div>
                        {r.criteria?.length > 0 && <CriteriaTable criteria={r.criteria} />}
                        <div className="aw-small qs-muted">
                          Run {r.runAt} by {r.runBy} · package {r.packageSource}
                          {!teaser && (
                            <>
                              {' · '}
                              <button
                                type="button"
                                className="aw-link"
                                onClick={() => openDeal(selectDeal, d.dealId)}
                              >
                                Open deal
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {crit && (
        <details
          id="buy-box"
          className="qs-criteria-cfg"
          data-testid="qs-criteria-cfg"
          open={critOpen}
          onToggle={(e) => setCritOpen(e.currentTarget.open)}
        >
          <summary>
            Buy-box criteria (
            {crit.orgOverride
              ? `org override by ${crit.orgOverride.updatedBy} ${crit.orgOverride.updatedAt}`
              : 'D2D default policy'}
            )
          </summary>
          <CriteriaEditor crit={crit} canEdit={auth.isAdmin} onSaved={(c) => setCrit(c)} />
        </details>
      )}
    </div>
  );
}

function CriteriaEditor({ crit, canEdit, onSaved }) {
  const [draft, setDraft] = useState(() =>
    Object.fromEntries(Object.entries(crit.criteria).map(([k, c]) => [k, { pass: c.pass, review: c.review, enabled: c.enabled }])),
  );
  const [msg, setMsg] = useState(null);
  const save = async () => {
    setMsg(null);
    try {
      const body = Object.fromEntries(
        Object.entries(draft).map(([k, v]) => [k, { pass: Number(v.pass), review: Number(v.review), enabled: v.enabled }]),
      );
      const c = await screenApi.setCriteria(body);
      onSaved(c);
      setMsg('Saved — screens re-run on next view.');
    } catch (e) {
      setMsg(e.message);
    }
  };
  const pctUnit = (u) => u === '%';
  return (
    <div>
      <table className="qs-table">
        <thead>
          <tr>
            <th>Criterion</th>
            <th>Meets</th>
            <th>Review</th>
            <th>On</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(crit.criteria).map(([k, c]) => (
            <tr key={k}>
              <td>
                {c.label} <span className="aw-small qs-muted">({c.direction === 'min' ? '≥' : '≤'}, {pctUnit(c.unit) ? 'fraction' : c.unit})</span>
              </td>
              {['pass', 'review'].map((f) => (
                <td key={f}>
                  <input
                    type="number"
                    step="any"
                    disabled={!canEdit}
                    value={draft[k][f]}
                    onChange={(e) => setDraft((d) => ({ ...d, [k]: { ...d[k], [f]: e.target.value } }))}
                    className="qs-crit-input"
                  />
                </td>
              ))}
              <td>
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={draft[k].enabled}
                  onChange={(e) => setDraft((d) => ({ ...d, [k]: { ...d[k], enabled: e.target.checked } }))}
                />
              </td>
              <td className="aw-small qs-muted">{c.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {canEdit ? (
        <button type="button" className="aw-btn" onClick={save}>
          Save criteria
        </button>
      ) : (
        <div className="aw-small qs-muted">Admins can edit criteria.</div>
      )}
      {msg && <div className="aw-small">{msg}</div>}
      {crit.audit?.length > 0 && (
        <div className="aw-small qs-muted">
          Audit:{' '}
          {crit.audit.slice(0, 5).map((a, i) => (
            <span key={i}>
              {a.by} {a.at}
              {i < Math.min(crit.audit.length, 5) - 1 ? '; ' : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
